import React, { FC, useId, useState } from 'react';
import { useLingui } from '@lingui/react/macro';
import styled from 'styled-components';

import Fieldset from '@rover/components/src/formFields/Fieldset';
import CollapseSmall from '@rover/icons/svg/collapse-small.svg';
import ExpandSmall from '@rover/icons/svg/expand-small.svg';
import { Box, Button } from '@rover/kibble/core';
import useTheme from '@rover/kibble/themes/useTheme';

import CheckBoxOption from './CheckBoxOption';

export type Checkbox = {
  attribute: string;
  label: string;
  tooltip: string;
  value?: string;
  onTooltipHover?: () => void;
};

export type Category = {
  category: string;
  rowsVisibleWhenCollapsed: number;
  checkboxes: Checkbox[];
};

export type Props = {
  category: Category;
  onChange: (updatedFilters: Record<string, any>, checkboxValue: any | null | undefined) => void;
  onExpandAndCollapse?: () => void;
  isChecked: (attribute: string, value: string | null | undefined) => boolean;
};

const StyledExpandSmall = styled(ExpandSmall)`
  fill: ${({ theme }) => theme.colors.interactive_link.primary_base};
  height: ${({ theme }) => theme.space['6x']};
  vertical-align: middle;
`;

const StyledCollapseSmall = styled(CollapseSmall)`
  fill: ${({ theme }) => theme.colors.interactive_link.primary_base};
  height: ${({ theme }) => theme.space['6x']};
  vertical-align: middle;
`;

const CheckBoxCategory: FC<Props> = ({
  onExpandAndCollapse = (): void => {},
  isChecked,
  onChange,
  category: { category, checkboxes, rowsVisibleWhenCollapsed },
}) => {
  const { t } = useLingui();
  const { colors, borderWidth } = useTheme();
  const checkboxCategoryId = useId();
  const [collapsed, setCollapsed] = useState(true);

  const expand = (): void => {
    setCollapsed(false);
    onExpandAndCollapse();
  };

  const collapse = (): void => {
    setCollapsed(true);
    onExpandAndCollapse();
  };

  const maxLength = collapsed ? rowsVisibleWhenCollapsed : checkboxes.length;

  return (
    <Fieldset aria-labelledby={checkboxCategoryId}>
      <Box
        id={checkboxCategoryId}
        marginTop="4x"
        marginBottom="2x"
        paddingBottom="1x"
        borderBottom={`${borderWidth.secondary} solid ${colors.border.primary}`}
        sx={{
          fontWeight: 'semibold',
          color: 'text.secondary',
        }}
      >
        {category}
      </Box>
      {checkboxes
        .slice(0, maxLength)
        .map(({ attribute, label, tooltip, value, onTooltipHover }) => (
          <CheckBoxOption
            key={`${attribute}${value ? `-${value}` : ''}`}
            checked={isChecked(attribute, value)}
            onChange={(checked, checkboxValue) =>
              onChange(
                {
                  [attribute]: checked,
                },
                checkboxValue
              )
            }
            onTooltipHover={onTooltipHover}
            label={label}
            tooltip={tooltip}
            value={value}
          />
        ))}
      {rowsVisibleWhenCollapsed < checkboxes.length && (
        <Button
          variant="flat"
          size="small"
          onClick={collapsed ? expand : collapse}
          aria-expanded={!collapsed}
          aria-controls={checkboxCategoryId}
          padding="0x"
        >
          {collapsed ? t`More` : t`Less`}
          {collapsed ? <StyledExpandSmall /> : <StyledCollapseSmall />}
        </Button>
      )}
    </Fieldset>
  );
};

export default CheckBoxCategory;
